import { grey } from "@mui/material/colors";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import * as React from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { ediphiPalette } from "./ui/styles/colors";
import "./ui/styles/global.css";
import Logger from "./clients/sentry";

Logger.init();
// Store start time in session storage for app load tracking
const startTime = (window as any).addinStartTime || Date.now();
sessionStorage.setItem("app_load_start", startTime.toString());

/* Create a theme after Office initializes and the CSS is loaded */
const createEdiphiTheme = () => {
  const primaryColor = ediphiPalette.brand500;
  const textColor = ediphiPalette.mainTextColor;
  const hoverColor = ediphiPalette.brand50;
  // Matches the body rule in taskpane.html, so React-rendered and raw HTML text fall back alike.
  const mainFontFamily = '"Inter", "Helvetica Neue", Arial, sans-serif';

  return createTheme({
    typography: {
      fontFamily: mainFontFamily,
      body1: {
        fontSize: "14px",
      },
      body2: {
        fontSize: "12px",
      },
      subtitle1: {
        fontSize: "16px",
      },
      subtitle2: {
        fontSize: "14px",
      },
    },
    /**
     * `error`, `warning`, `success` and `info` are deliberately NOT declared: MUI derives `Alert`'s
     * fill and text from `palette[severity]`, and the stock MUI severity colours are the ones we
     * want on alerts. Declaring them here would repaint every `Alert` — see `ui/shared/dialog` and
     * `ui/shared/loaders/LinearLoader`. Use `ediphiPalette.mainRed` / `mainYellow` directly if you
     * need an Ediphi severity colour somewhere that isn't an Alert.
     */
    palette: {
      primary: {
        main: primaryColor,
      },
      divider: ediphiPalette.borderColor,
      background: {
        default: "transparent",
        paper: ediphiPalette.surface,
      },
      text: {
        primary: textColor,
        secondary: ediphiPalette.greyText,
        disabled: ediphiPalette.textDisabled,
      },
    },
    components: {
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            width: "100%",
          },
          inputRoot: {
            paddingLeft: 0,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: "4px",
            textTransform: "none",
            fontWeight: 500,
            // No `height` here. It was pinned to 44px, which every styled button then had to undo —
            // and it broke `CardButton`, whose height is content-driven. A height belongs to a
            // particular button, not to all of them.
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 500,
            minWidth: "auto",
            padding: "6px 16px",
            "&.Mui-selected": {
              color: ediphiPalette.black900,
              fontWeight: "bold",
              backgroundColor: "transparent",
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: ediphiPalette.black900,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderColor: ediphiPalette.borderInput,
          },
          root: {
            borderRadius: "4px",
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: ediphiPalette.brand800,
            },
            "&.Mui-focused": {
              boxShadow: "0 0 0 2px rgba(5, 145, 255, 0.1)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: ediphiPalette.brand600,
              borderWidth: 1,
            },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderRadius: "4px",
            backgroundColor: "transparent",
            "&:hover": {
              backgroundColor: hoverColor,
            },
            "&.Mui-focused": {
              backgroundColor: "transparent",
            },
          },
          adornedEnd: {
            paddingRight: "50px !important",
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: "16px",
            color: grey[700], // Set label color
            "&.Mui-focused": {
              color: textColor, // Same font and color when focused
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            width: "100%",
          },
        },
      },
    },
  });
};

const rootElement: HTMLElement | null = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;

const script = document.createElement("script");
script.src = "https://appsforoffice.microsoft.com/lib/1/hosted/office.js";
script.type = "text/javascript";
document.head.appendChild(script);

// Prevents race condition where Office.js is not fully loaded while Office.onReady is called
script.onload = () => {
  /* Render application after Office initializes */
  Office.onReady(async () => {
    const endTime = Date.now();
    const totalDuration = (endTime - startTime) / 1000;
    const loadTime = `${totalDuration} seconds`;
    console.info("Load time:", loadTime);

    OfficeExtension.config.extendedErrorLogging = true;
    // Guarded because it runs before root.render: the manifests ask for ExcelApi/ExcelApiOnline, not
    // TaskPaneApi, so a host without it would throw here and leave the pane blank rather than just
    // its default width.
    if (Office.context.requirements.isSetSupported("TaskPaneApi", "1.1")) {
      Office.extensionLifeCycle.taskpane.setWidth(400);
    }

    const theme = createEdiphiTheme(); // Create the theme once the document is ready
    if (root) {
      root.render(
        <ThemeProvider theme={theme}>
          <App />
        </ThemeProvider>
      );
    }
  });
};

if ((module as any).hot) {
  (module as any).hot.accept("./App", () => {
    const { App: NextApp } = require("./App");
    const theme = createEdiphiTheme();
    if (root) {
      root.render(
        <ThemeProvider theme={theme}>
          <NextApp />
        </ThemeProvider>
      );
    }
  });
}
